import { reverse } from 'named-urls'
import React from 'react'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  ButtonClean,
  Fieldset,
  HeadContent,
  Input,
  Loading,
  RadioButton,
  Subsection,
} from '@fourthwall/components'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { routing } from '@utils/routing'

import { useCurrentShopQuery } from '../../../../../hooks/useCurrentShopQuery'
import { useAccountSettingsQuery } from '../../hooks/useAccountSettingsQuery'
import { useUpdateAccountSettingsPwasMutation } from '../../hooks/useUpdateAccountSettingsPwasMutation'
import { Bar } from './components/Bar'
import { validationSchema } from './consts'
import * as S from './styled'
import type { FormValues } from './types'
import { FormFields } from './types'
import { getUpdateAccountSettingsPwasPayload } from './utils'

export const MobileApp = () => {
  const { accountSettingsQuery } = useAccountSettingsQuery()
  const { updateAccountSettingsPwasMutation } = useUpdateAccountSettingsPwasMutation()
  const { currentShopQuery } = useCurrentShopQuery()

  const formik = useFormik<FormValues>({
    initialValues: {
      appNameSetting: accountSettingsQuery.data?.pwa.customName ? 'custom' : 'default',
      appName: accountSettingsQuery.data?.pwa.customName,
    },
    validationSchema,
    onSubmit: (values) => {
      updateAccountSettingsPwasMutation.mutate([getUpdateAccountSettingsPwasPayload(values)], {
        onSuccess: () => {
          accountSettingsQuery.refetch()
        },
      })
    },
    enableReinitialize: true,
  })

  const isLoading =
    currentShopQuery.isLoading ||
    accountSettingsQuery.isLoading ||
    updateAccountSettingsPwasMutation.isLoading

  return (
    <form onSubmit={formik.handleSubmit}>
      <HeadContent title="Mobile app" />
      <Loading isLoading={isLoading}>
        <Subsection title="Mobile app">
          <Fieldset>
            <Fieldset.Row>
              <Fieldset.Item>
                We automatically create an app for you that your members can install to receive
                notifications about new posts. Instructions for installing the app will show up when
                members visit your site on their phone. Apps will not show up in the Apple or
                Android app stores.{' '}
                <ButtonClean
                  href="https://help.fourthwall.com/hc/en-us/articles/20092051359003"
                  label="Learn more"
                  target="_blank"
                  withUnderline
                />
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <S.Text>App name:</S.Text>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  checkedValue="default"
                  label={`Same as site name (${currentShopQuery.data?.name})`}
                  {...formik.getFieldProps(FormFields.appNameSetting)}
                />
              </Fieldset.Item>
            </Fieldset.Row>
            <Fieldset.Row>
              <Fieldset.Item>
                <RadioButton
                  checkedValue="custom"
                  label="Custom name"
                  {...formik.getFieldProps(FormFields.appNameSetting)}
                />
              </Fieldset.Item>
            </Fieldset.Row>
            {formik.values.appNameSetting === 'custom' && (
              <Fieldset.Row inner>
                <Fieldset.Item>
                  <Input label="Custom app name" {...formik.getFieldProps(FormFields.appName)} />
                </Fieldset.Item>
              </Fieldset.Row>
            )}
            <Fieldset.Row>
              <S.PreviewContainer>
                <S.Text>Bottom bar preview:</S.Text>
                <Bar
                  title={
                    formik.values.appNameSetting === 'custom'
                      ? formik.values.appName || currentShopQuery.data?.name
                      : currentShopQuery.data?.name
                  }
                />
                <S.Text>
                  Members will see app installation instructions at the bottom when browsing your
                  site on their phone. Your app icon is the same as your{' '}
                  <ButtonClean
                    label="favicon"
                    to={reverse(routing.themeEditorV2.general.sections, { sectionId: 0 })}
                    withUnderline
                    appearance="secondary"
                  />
                  .
                </S.Text>
              </S.PreviewContainer>
            </Fieldset.Row>
          </Fieldset>
        </Subsection>
      </Loading>
      <ConfirmBar
        isOpen={formik.dirty}
        isLoading={isLoading}
        onConfirm={formik.submitForm}
        onCancel={formik.resetForm}
      />
      <LeavingGuard when={formik.dirty} isValid={formik.isValid} onSave={formik.submitForm} />
    </form>
  )
}
